import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../services/api';

export type ModuleStatus = 'not_offered' | 'offered' | 'trialing' | 'trial_expired' | 'subscribed' | 'dismissed';

export interface MenuConfig {
  icon: string;
  path: string;
  label_key: string;
  sort_order: number;
}

export interface ModuleFeatureSummary {
  status: ModuleStatus;
  /**
   * T1391/T1424: **»preizkus je mogoče ZDAJ začeti«**, ne »ta modul ima
   * nastavljene preizkusne dni«.
   *
   * ⚠️ Pomen polja se je s T1391 spremenil. Zaledje ga izračuna kot
   * konjunkcijo (`apps/api/v1/views/auth.py::MeModulesView`):
   * org-lastna zastavica iz T1350 ∧ `module.trial_days > 0` ∧ status je
   * `offered`/`not_offered`. Odjemalcu torej ni treba (in ne sme) ničesar
   * dodajati — prej je moral obe polovici združiti sam in je org-zastavico
   * jemal iz `GET organizations/`, ki ob odprtju modala ni bil nujno naložen.
   *
   * Polje je prisotno samo pri statusih `offered`/`trial_expired`/`not_offered`;
   * drugod ga zaledje izpusti. Odsotnost pomeni »ne vem«, ne »na voljo« —
   * `ModuleTrialModal` ob njej gumba ne izriše.
   */
  trial_available?: boolean;
  trial_ends_at?: string | null;
  features: Record<string, boolean>;
  limits: Record<string, number | null>;
  menu_config?: MenuConfig;
  /**
   * T1228: ali ima **ta uporabnik** sedež za modul (ne le organizacija dostop).
   *
   * T2436: `false` dobi samo še lastnik/administrator organizacije (modul
   * bere); članu brez sedeža zaledje modul izpusti (`seatlessModules`).
   * Vmesnik po tem polju **pojasnjuje**, zakaj je nekaj zaklenjeno.
   */
  has_seat?: boolean;
}

interface ModuleStoreState {
  modules: Record<string, ModuleFeatureSummary>;
  /**
   * T1424 — org-lastna zastavica preizkusa (T1350), kot jo vrne
   * `GET me/modules/` v gnezdu `organization` (T1391). Zaledje jo pošlje
   * **tudi na praznem odgovoru**, prav zato, da »ni polja« ni mogoče zamenjati
   * s »preizkus ni na voljo«.
   *
   * Privzetek je `undefined` = »ne vem« in **ne** `true`. To je edina razlika,
   * zaradi katere popravek ni le premik vira: `ModuleTrialModal` je prej
   * neznano bral kot »na voljo« in ponudil gumb, ki vrne 409
   * `TRIAL_ALREADY_USED`.
   *
   * Za razliko od `modules` se **ne vztraja** (gl. `partialize`): star zapis iz
   * `localStorage` bi po menjavi organizacije trdil nekaj o napačni.
   */
  organizationTrialAvailable?: boolean;
  /**
   * T2436 — moduli, ki jih zaledje iz `modules` izpusti, ker uporabnik zanje
   * nima sedeža (brez sedeža = brez dostopa). Loči »nimaš sedeža« od »modula
   * ni« — oboje je na `modules` videti enako (manjkajoč vpis). Ne vztraja se
   * (gl. `partialize`), ista utemeljitev kot pri `organizationTrialAvailable`.
   */
  seatlessModules: string[];
  isLoading: boolean;
  fetchModules: (orgSlug?: string) => Promise<void>;
  hasModule: (slug: string) => boolean;
  hasFeature: (slug: string, key: string) => boolean;
  getLimit: (slug: string, key: string) => number | null | undefined;
  hasSeat: (slug: string) => boolean;
  /** T2436: ali je modul izpuščen zaradi manjkajočega sedeža. */
  isSeatless: (slug: string) => boolean;
}

export const useModuleStore = create<ModuleStoreState>()(
  persist(
    (set, get) => ({
      modules: {},
      organizationTrialAvailable: undefined,
      seatlessModules: [],
      isLoading: false,

      fetchModules: async (orgSlug?: string) => {
        set({ isLoading: true });
        try {
          const url = orgSlug ? `me/modules/?organization=${orgSlug}` : `me/modules/`;
          const response = await api.get(url);
          // Samo prava logična vrednost šteje; karkoli drugega (starejše
          // zaledje brez gnezda, niz iz posrednika) ostane »ne vem«.
          const orgFlag = response.data?.organization?.trial_available;
          const seatless = response.data?.seatless_modules;
          set({
            modules: response.data.modules,
            seatlessModules: Array.isArray(seatless) ? seatless : [],
            organizationTrialAvailable:
              typeof orgFlag === 'boolean' ? orgFlag : undefined,
          });
        } catch (err) {
          console.error('Failed to fetch modules', err);
          // Zastavica pade nazaj na »ne vem«: obdržati staro bi pomenilo
          // trditi nekaj o organizaciji na podlagi poizvedbe, ki ni uspela.
          set({ modules: {}, organizationTrialAvailable: undefined, seatlessModules: [] });
        } finally {
          set({ isLoading: false });
        }
      },

      hasModule: (slug: string) => {
        const mod = get().modules[slug];
        if (!mod) return false;
        return mod.status === 'subscribed' || mod.status === 'trialing';
      },

      hasFeature: (slug: string, key: string) => {
        const mod = get().modules[slug];
        if (!mod) return false;
        if (mod.status !== 'subscribed' && mod.status !== 'trialing') return false;
        return !!mod.features?.[key];
      },

      getLimit: (slug: string, key: string) => {
        const mod = get().modules[slug];
        if (!mod) return undefined;
        if (mod.status !== 'subscribed' && mod.status !== 'trialing') return undefined;
        return mod.limits?.[key];
      },

      /**
       * T1228: ali uporabnik drži sedež za modul.
       *
       * **Neznano ni isto kot »brez sedeža«.** Manjkajoče polje (star vztrajen
       * zapis iz `persist`, ki ga prvi `fetchModules` še ni osvežil) vrne
       * `true`. Odsotnost sedeža uveljavlja zaledje — `resolve_project_access`
       * zniža veljavno vlogo na `viewer` in vsi pragovi to podedujejo; ta
       * selektor obstaja **samo zato, da vmesnik zna povedati ZAKAJ**. Če bi
       * neznano šteli za odsotnost, bi plačan uporabnik ob vsakem nalaganju za
       * hip izgubil gumbe — vidna okvara, ki bi jo zamenjal za pokvarjeno
       * platformo, medtem ko je najhujša posledica nasprotne izbire 403, ki ga
       * zaledje tako ali tako ustavi.
       */
      hasSeat: (slug: string) => {
        const mod = get().modules[slug];
        if (!mod || mod.has_seat === undefined) return true;
        return mod.has_seat;
      },

      isSeatless: (slug: string) => {
        // Vpis v `modules` ima prednost: lastnik/administrator brez sedeža
        // modul obdrži (bere), zato ga zaledje ne navede kot izpuščenega.
        if (get().modules[slug]) return false;
        return get().seatlessModules.includes(slug);
      }
    }),
    {
      name: 'module-store',
      partialize: (state) => ({ modules: state.modules }),
    }
  )
);
